import React from "react";
import PropTypes from "prop-types";
import "../css/charts.css";

const LineChart = ({ title, data, xKey, yKey, small = false, accentColor = "#4F46E5" }) => {
  const safeData = Array.isArray(data) ? data : [];
  if (!safeData.length) {
    return null;
  }

  const dateFormatter = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
  const dayInMs = 24 * 60 * 60 * 1000;
  const fallbackStart = new Date();
  fallbackStart.setHours(0, 0, 0, 0);
  fallbackStart.setDate(fallbackStart.getDate() - (safeData.length - 1));
  const fallbackBaseTimestamp = fallbackStart.getTime();

  const formatDateLabel = (item, rawLabel, index) => {
    if (item?.dateLabel) {
      return item.dateLabel;
    }
    const candidate = item?.date ?? rawLabel;
    const parsed = Date.parse(candidate);
    if (!Number.isNaN(parsed)) {
      return dateFormatter.format(new Date(parsed));
    }
    const fallbackDate = new Date(fallbackBaseTimestamp + index * dayInMs);
    return dateFormatter.format(fallbackDate);
  };

  const yValues = safeData.map((item) => {
    const numericValue = Number(item?.[yKey]);
    return Number.isFinite(numericValue) ? numericValue : 0;
  });

  const maxValue = Math.max(...yValues);
  const minValue = Math.min(...yValues);
  const range = maxValue - minValue;
  const safeRange = range === 0 ? 1 : range;

  const chartWidth = 640;
  const chartHeight = small ? 220 : 280;
  const padding = 40;
  const verticalSpace = chartHeight - padding * 2;
  const baselineY = chartHeight - padding;
  const labelYPosition = Math.min(chartHeight - 8, baselineY + 24);
  const contentWidth = chartWidth - padding * 2;
  const xSpacing = safeData.length === 1 ? 0 : contentWidth / (safeData.length - 1);

  const points = safeData.map((item, index) => {
    const rawLabel = item?.[xKey] ?? `#${index + 1}`;
    const value = yValues[index];
    const normalized = (value - minValue) / safeRange;
    const x = safeData.length === 1 ? chartWidth / 2 : padding + xSpacing * index;
    const y = baselineY - normalized * verticalSpace;
    const label = formatDateLabel(item, rawLabel, index);
    return { x, y, value, label };
  });

  const linePath = points.reduce((path, point, index) => {
    if (index === 0) {
      return `M ${point.x} ${point.y}`;
    }
    return `${path} L ${point.x} ${point.y}`;
  }, "");

  const areaPath = points.length
    ? `${linePath} L ${points[points.length - 1].x} ${baselineY} L ${points[0].x} ${baselineY} Z`
    : "";

  const tickCount = 4;
  const yTicks = Array.from({ length: tickCount + 1 }, (_, index) => {
    const ratio = index / tickCount;
    const value = maxValue - ratio * range;
    const y = padding + ratio * verticalSpace;
    return {
      label: Number.isFinite(value) ? Number(value.toFixed(range <= 10 ? 1 : 0)) : 0,
      y,
    };
  });

  const sanitizedTitle = (title || "chart").toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const accentKey = accentColor.replace(/[^a-z0-9]/gi, "");
  const gradientId = `lineGradient-${sanitizedTitle}-${safeData.length}-${accentKey}`;
  const shadowId = `lineShadow-${sanitizedTitle}-${safeData.length}-${accentKey}`;

  return (
    <div className="chart-card">
      <h3 className="chart-title">{title}</h3>
      <div className={`line-chart-container ${small ? "small" : ""}`}>
        <svg
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          className="line-chart-svg"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={accentColor} stopOpacity="0.35" />
              <stop offset="100%" stopColor={accentColor} stopOpacity="0.05" />
            </linearGradient>
            <filter id={shadowId} x="-10%" y="-20%" width="140%" height="180%">
              <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor={accentColor} floodOpacity="0.3" />
            </filter>
          </defs>

          {yTicks.map((tick, index) => (
            <line
              key={`grid-${index}`}
              x1={padding}
              y1={tick.y}
              x2={chartWidth - padding}
              y2={tick.y}
              stroke="#E5E7EB"
              strokeWidth="1"
              strokeDasharray={index === 0 ? "0" : "4 6"}
            />
          ))}

          <line
            x1={padding}
            y1={padding}
            x2={padding}
            y2={baselineY}
            stroke="#D1D5DB"
            strokeWidth="1.5"
          />
          <line
            x1={padding}
            y1={baselineY}
            x2={chartWidth - padding}
            y2={baselineY}
            stroke="#D1D5DB"
            strokeWidth="1.5"
          />

          {areaPath && (
            <path d={areaPath} fill={`url(#${gradientId})`} stroke="none" opacity="0.9" />
          )}

          <path
            d={linePath}
            fill="none"
            stroke={accentColor}
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            filter={`url(#${shadowId})`}
          />

          {points.map((point, index) => (
            <g key={`point-${index}`}>
              <circle
                cx={point.x}
                cy={point.y}
                r="5"
                fill="#fff"
                stroke={accentColor}
                strokeWidth="2"
                className="line-point-circle"
              />
              <text
                x={point.x}
                y={point.y - 10}
                textAnchor="middle"
                className="line-point-label"
              >
                {point.value}
              </text>
            </g>
          ))}

          {points.map((point, index) => (
            <text
              key={`date-label-${index}`}
              x={point.x}
              y={labelYPosition}
              textAnchor="middle"
              className="line-point-date"
            >
              {point.label}
            </text>
          ))}
        </svg>

        <div className="line-chart-y-axis">
          {yTicks.map((tick, index) => (
            <span key={`tick-${index}`} className="y-axis-label">
              {tick.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

LineChart.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  xKey: PropTypes.string.isRequired,
  yKey: PropTypes.string.isRequired,
  small: PropTypes.bool,
  accentColor: PropTypes.string,
};

export default LineChart;
